// Generic offline sync engine: queues action API calls made offline and replays them
// automatically (and silently) when connectivity returns. Feature-agnostic — callers
// enqueue a self-describing request; the engine dispatches it through Apibase on sync.

import { v4 as uuidv4 } from "uuid";
import { GetRequest, PostRequest } from "../Api/Apibase";
import { outboxDB } from "../IndexedDB/outbox.idb";
import { OUTBOX_CHANGED_EVENT } from "../../Shell/Utils/appEvents";

let processing = false;
let initialized = false;

// Caps auto-retries and bounds a single request so a hung call can't freeze the queue.
const MAX_ATTEMPTS = 5;
const REQUEST_TIMEOUT_MS = 30000;

function emitChange() {
    try {
        window.dispatchEvent(new Event(OUTBOX_CHANGED_EVENT));
    } catch {
        // no-op (non-browser env)
    }
}

function withTimeout(promise, ms) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("Request timed out")), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Adds (or refreshes, via dedupeKey) a request to the outbox and returns its id.
export async function enqueue({ type, method = "POST", url, payload = null, request = {}, meta = {}, dedupeKey }) {
    const now = Date.now();

    if (dedupeKey) {
        const existing = await outboxDB.getByDedupeKey(dedupeKey);
        const active = (existing || []).find((i) => i.status !== "succeeded");
        if (active) {
            await outboxDB.update(active.id, {
                type, method, url, payload, request, meta,
                status: "queued", lastError: null, updatedAt: now,
            });
            emitChange();
            return active.id;
        }
    }

    const item = {
        id: uuidv4(),
        type, method, url, payload, request, meta,
        dedupeKey: dedupeKey || `${type}:${now}`,
        status: "queued",
        attempts: 0,
        lastError: null,
        createdAt: now,
        updatedAt: now,
    };
    await outboxDB.add(item);
    emitChange();
    return item.id;
}

async function dispatchRequest(item) {
    const r = item.request || {};
    if ((item.method || "POST").toUpperCase() === "GET") {
        return GetRequest(item.url, r.authorization, r.gsidRequired, r.authToken);
    }
    return PostRequest(item.url, item.payload ?? null, r.authorization, r.gsidRequired, r.checkResponseType);
}

// Drains all queued items in creation order. Succeeded items are removed; business
// failures are marked "failed" (manual retry); missing responses stay queued for retry.
export async function processQueue() {
    if (processing) return;
    if (!window.navigator.onLine) return;

    processing = true;
    try {
        // Reclaim items orphaned in "syncing" by a previous reload/crash/hung request.
        await outboxDB.requeueStuck();

        const items = await outboxDB.getReplayable();
        for (const item of items) {
            await outboxDB.update(item.id, { status: "syncing", updatedAt: Date.now() });
            emitChange();

            let response;
            try {
                response = await withTimeout(dispatchRequest(item), REQUEST_TIMEOUT_MS);
            } catch {
                response = undefined;
            }

            const attempts = (item.attempts || 0) + 1;

            if (response === undefined || response === null) {
                const exhausted = attempts >= MAX_ATTEMPTS;
                await outboxDB.update(item.id, {
                    status: exhausted ? "failed" : "queued",
                    attempts,
                    lastError: exhausted
                        ? "No response after several attempts. Tap retry."
                        : "No response — will retry.",
                    updatedAt: Date.now(),
                });
            } else if (response.error) {
                await outboxDB.update(item.id, {
                    status: "failed",
                    attempts,
                    lastError: String(response.error),
                    updatedAt: Date.now(),
                });
            } else {
                await outboxDB.remove(item.id);
            }
            emitChange();
        }
    } finally {
        processing = false;
    }
}

export async function retryItem(id) {
    await outboxDB.update(id, { status: "queued", lastError: null, updatedAt: Date.now() });
    emitChange();
    processQueue();
}

export async function removeItem(id) {
    await outboxDB.remove(id);
    emitChange();
}

// Registers connectivity listeners and drains any pending queue. Safe to call once at app start.
export function initOfflineSync() {
    if (initialized) return;
    initialized = true;
    window.addEventListener("online", () => { processQueue(); });
    if (window.navigator.onLine) processQueue();
}
